import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, CheckCircle, Zap, TrendingUp } from 'lucide-react';
import SEO from '../../components/SEO';

const StaffAugmentation = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: Zap,
      title: 'Rapid Deployment',
      description: 'Get skilled resources on board within days, not months',
    },
    {
      icon: TrendingUp,
      title: 'Flexible Scaling',
      description: 'Scale your team up or down based on project needs',
    },
    {
      icon: CheckCircle,
      title: 'Vetted Professionals',
      description: 'Access pre-screened IT experts ready to contribute',
    },
  ];

  const features = [
    'Rapid deployment of skilled resources',
    'Flexible contract terms (short-term to long-term)',
    'Expertise in modern technologies and frameworks',
    'Reduced hiring costs and overhead',
    'Seamless integration with existing teams',
    'On-demand technical expertise',
    'No long-term commitments required',
    'Replace non-performing resources quickly',
  ];

  const useCases = [
    {
      title: 'Project-Based Work',
      description: 'Need extra hands for a specific project with a defined timeline',
    },
    {
      title: 'Skill Gap Filling',
      description: 'Require specialized expertise your team currently lacks',
    },
    {
      title: 'Peak Period Support',
      description: 'Handle increased workload during busy seasons',
    },
    {
      title: 'Technology Migration',
      description: 'Expert guidance for platform or technology transitions',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="IT Staff Augmentation Services"
        description="Scale your team quickly with experienced IT professionals. Flexible staff augmentation solutions for short-term and long-term projects. Get skilled developers, engineers, and analysts on demand."
        keywords="IT staff augmentation, contract developers, temporary IT staff, project-based hiring, IT contractors, software development resources"
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 opacity-60"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-400/20 via-cyan-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-slate-200">
              <span className="text-sm font-medium text-slate-700">Staff Augmentation</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Scale your team with{' '}
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                expert IT professionals
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Quickly augment your team with experienced developers, engineers, and analysts on a contract or project basis. Get the skills you need, exactly when you need them.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
              >
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <a
                href="tel:+91-9121711433"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white text-slate-900 rounded-full border-2 border-slate-200 hover:border-slate-300 transition-all duration-200 font-semibold text-lg"
              >
                <span>Call: +91-9121711433</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        ref={(el) => (sectionsRef.current[0] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Why choose staff augmentation?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              The flexible way to access top IT talent without the overhead of permanent hiring
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        ref={(el) => (sectionsRef.current[1] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                What you get with our staff augmentation
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Access a pool of pre-vetted IT professionals who can seamlessly integrate with your existing team and contribute from day one.
              </p>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#f9a825] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-12 rounded-3xl shadow-2xl">
              <Users className="w-32 h-32 text-white mx-auto opacity-30" />
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section
        ref={(el) => (sectionsRef.current[2] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Perfect for these scenarios
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Staff augmentation is ideal when you need flexibility and expertise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-3">{useCase.title}</h3>
                <p className="text-slate-600 leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={(el) => (sectionsRef.current[3] = el)}
        className="py-24 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-0 transition-opacity duration-1000 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to augment your team?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's discuss your staffing needs and find the perfect IT professionals for your projects
          </p>
          <Link
            to="/contact-us"
            className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
          >
            <span>Contact Us Today</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default StaffAugmentation;
